"use client";

import { signIn } from "@/lib/auth/auth-client";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "../FormElements/checkbox";

/** Cor de destaque do login — teal do design "Sequence". */
const ACCENT = "#025864";

const labelCls = "mb-1.5 block text-xs font-medium text-dark-5 dark:text-dark-6";
const inputCls =
  "w-full rounded-lg border border-[var(--sg-border)] bg-[var(--sg-bg-surface)] px-3.5 py-2.5 text-sm text-[var(--sg-text-primary)] outline-none transition-colors placeholder:text-[var(--sg-text-faint)] focus:border-primary disabled:cursor-not-allowed disabled:opacity-60";

export default function SigninWithPassword() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [data, setData] = useState({
    email: process.env.NEXT_PUBLIC_DEMO_USER_MAIL || "",
    password: process.env.NEXT_PUBLIC_DEMO_USER_PASS || "",
    remember: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const callbackURL = searchParams.get("callbackUrl") || "/";

      const result = await signIn.email({
        email: data.email,
        password: data.password,
        rememberMe: data.remember,
      });

      if (!result.data) {
        throw new Error(result.error?.message || "E-mail ou senha inválidos");
      }

      toast.success("Login realizado com sucesso");
      router.push(callbackURL);
      router.refresh();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Não foi possível entrar";
      setError("E-mail ou senha inválidos");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    // method="post" é uma salvaguarda: se o submit nativo disparar (antes da
    // hidratação do JS ou com JS desabilitado), o navegador envia os dados no
    // corpo do POST em vez de anexá-los à URL como query string (GET), evitando
    // vazamento de e-mail/senha na URL, no histórico e em logs.
    <form method="post" onSubmit={handleSubmit}>
      {/* E-mail */}
      <div className="mb-4">
        <label htmlFor="email" className={labelCls}>
          E-mail
        </label>
        <input
          id="email"
          type="email"
          name="email"
          placeholder="voce@empresa.com.br"
          value={data.email}
          onChange={handleChange}
          autoComplete="email"
          className={inputCls}
        />
      </div>

      {/* Senha com toggle mostrar/ocultar */}
      <div className="mb-5">
        <label htmlFor="password" className={labelCls}>
          Senha
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPass ? "text" : "password"}
            name="password"
            placeholder="Digite sua senha"
            value={data.password}
            onChange={handleChange}
            autoComplete="current-password"
            className={`${inputCls} pr-16`}
          />
          <button
            type="button"
            onClick={() => setShowPass((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-dark-5 transition hover:text-dark dark:text-dark-6 dark:hover:text-white"
            aria-label={showPass ? "Ocultar senha" : "Mostrar senha"}
          >
            {showPass ? "Ocultar" : "Mostrar"}
          </button>
        </div>
      </div>

      {/* Lembrar de mim + Esqueci minha senha */}
      <div className="mb-6 flex items-center justify-between gap-2 text-[13px] font-medium">
        <Checkbox
          label="Lembrar de mim"
          name="remember"
          withIcon="check"
          minimal
          radius="md"
          onChange={(e) =>
            setData({
              ...data,
              remember: e.target.checked,
            })
          }
        />

        <Link
          href="#"
          className="outline-0 transition hover:underline"
          style={{ color: ACCENT }}
        >
          Esqueci minha senha
        </Link>
      </div>

      {/* Entrar */}
      <button
        type="submit"
        disabled={loading}
        style={{ backgroundColor: ACCENT }}
        className="flex h-[42px] w-full cursor-pointer items-center justify-center gap-2 rounded-lg text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
      >
        Entrar
        {loading && (
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-t-transparent" />
        )}
      </button>

      {error && <p className="mt-3 text-sm text-red">{error}</p>}

      {/* Recuperação de senha pelo escritório contábil */}
      <p className="mt-5 text-center text-[13px] text-dark-5 dark:text-dark-6">
        Esqueceu a senha? Entre em contato com a sua contabilidade.
      </p>
    </form>
  );
}
